/**
 * DimensionRadar — Interactive six-dimension contribution radar
 *
 * Visualises a user's contribution shape across six dimensions:
 *   Mind, Voice, Heart, Body, Wings, Roots
 *
 * Design: D3 Frosted Layers style with Structural Hex grid.
 * Animation: Inside-out build → dots → wire → slide left → labels → text → ambient life.
 *
 * Tier data is supplied via the `tiers` prop from useApiImpactSignature.
 * When no impact data exists, all dimensions start at 'none' (center).
 *
 * Follows CLAUDE.md conventions:
 *   - useAppTranslation (i18n keys to be scaffolded)
 *   - CSS custom property tokens only
 *   - CSS logical properties for RTL
 *   - debugMode gating
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// import GrowthOpenRoles from './GrowthOpenRoles'; // Temporarily removed — will relocate
import type { DimensionKey } from '../ProjectTeam/projectTeamTypes';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { Brain, Volume2, Heart, User, Bird, TreeDeciduous, Lock } from 'lucide-react';
import DimensionMedal from './DimensionMedal';
import { overallSignature } from '../../api/impactSignature';
import DimensionMedalModal from './DimensionMedalModal';
import { useApiImpactActivities } from '../../api/useApiImpactActivities';
import { localizedActivity, gateActivitiesByRung, type Rung } from '../../api/impactActivities';
import styles from './DimensionRadar.module.css';

/* ── Dimension icon mapping (lucide-react) ── */
const DIM_ICONS: Record<string, React.FC<{ color: string; size: number }>> = {
  mind:  ({ color, size }) => <Brain color={color} size={size} strokeWidth={1.8} />,
  voice: ({ color, size }) => <Volume2 color={color} size={size} strokeWidth={1.8} />,
  heart: ({ color, size }) => <Heart color={color} size={size} strokeWidth={1.8} />,
  body:  ({ color, size }) => <User color={color} size={size} strokeWidth={1.8} />,
  wings: ({ color, size }) => <Bird color={color} size={size} strokeWidth={1.8} />,
  roots: ({ color, size }) => <TreeDeciduous color={color} size={size} strokeWidth={1.8} />,
};

/* ── Types ── */

type TierLevel = 'none' | 'bronze' | 'silver' | 'gold' | 'platinum';

/**
 * Per-dimension CONCEPT copy. Static by design.
 *
 * DEV-1480 split this file's copy into two kinds, and the distinction is
 * load-bearing:
 *
 *   CONCEPT (here)  — what a dimension IS. Describes, promises nothing, implies
 *                     no credit. Safe to hardcode: it never goes stale against
 *                     the emission engine because it makes no claim about it.
 *
 *   CREDIT (the DB) — what actually EARNS credit. Comes from impact_activities
 *                     (enabled rows only), via useApiImpactActivities.
 *
 * The old `growth: string[]`, `nudge` and `roles` fields were CREDIT copy wearing
 * concept clothes: hardcoded suggestions like "Complete learning modules" that
 * emitted absolutely nothing. They are gone. Do not add them back — if you want
 * to tell a member an act earns credit, add the act to the catalog and wire a
 * fact detector for it.
 *
 * `roles` also carried a THIRD rung vocabulary ('Explorer → Investigator →
 * Contributor → Steward', 'Patron → Leader') that matched neither DEV-1301's
 * ladder nor the DB's CHECK constraint. Removed rather than reconciled: the rung
 * of an activity now travels with the activity, from the catalog.
 */
interface DimensionData {
  name: string;
  descriptor: string;
  color: string;
  bg: string;
  /** What this dimension means. Concept copy — never a credit promise. */
  meaning: string;
}

/**
 * Display mode:
 *   - 'dashboard': Full interactive coaching — overview text, growth tips,
 *     nudge cards, legend. Used on the Impact Dashboard (MyImpactPage).
 *   - 'profile': Presentational read-only — shows the radar shape and legend
 *     but omits coaching details (growth tips, nudge cards). Used on the
 *     UserProfile Impact tab.
 *
 * Both modes render the same animated radar; the difference is in the
 * right-side content panel.
 */
type RadarMode = 'dashboard' | 'profile';

interface DimensionRadarProps {
  /** Per-dimension tier data. Defaults to sample data if not provided. */
  tiers?: Record<string, TierLevel>;
  /** Controls what the right panel shows. Default: 'dashboard'. */
  mode?: RadarMode;
  /** Community GUIDs for fetching open roles in dashboard mode. */
  communityGuids?: string[];
  /** Fired when a dimension is selected or deselected (null). */
  onDimensionSelect?: (dimension: DimKey | null) => void;
  /**
   * DEV-1489 Slice C — the VIEWER's rung, in the impact vocabulary
   * (explorer | contributor | champion | governor). Acts at/below it render
   * live; the next rung's acts render locked-and-explained; higher rungs are
   * hidden. UNDEFINED = no gating (every enabled act shown live) — the default,
   * so mounts that don't opt in are unchanged.
   */
  viewerRung?: Rung;
  debugMode?: boolean;
}

/* ── Constants ── */

/**
 * Regular hexagon geometry.
 * Center: (210, 190), max radius: 140.
 * Tier rings at 25% intervals: Bronze=35, Silver=70, Gold=105, Platinum=140.
 */
const CX = 210;
const CY = 190;

const TIER_RADII: Record<TierLevel, number> = {
  none: 0,
  bronze: 35,
  silver: 70,
  gold: 105,
  platinum: 140,
};

/** Hex vertex angles (clockwise from top): 0=Mind, 1=Voice, 2=Heart, 3=Body, 4=Wings, 5=Roots */
const ANGLES_DEG = [90, 30, -30, -90, -150, 150]; // measured from +X axis
const DIM_KEYS = ['mind', 'voice', 'heart', 'body', 'wings', 'roots'] as const;
type DimKey = typeof DIM_KEYS[number];

function hexVertex(r: number, angleDeg: number): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  return [
    Math.round((CX + r * Math.cos(rad)) * 10) / 10,
    Math.round((CY - r * Math.sin(rad)) * 10) / 10,
  ];
}

function hexPolygonPoints(r: number): string {
  return ANGLES_DEG.map((a) => hexVertex(r, a).join(',')).join(' ');
}

/** Default empty tiers — all dimensions start at center (no impact yet) */
const EMPTY_TIERS: Record<string, TierLevel> = {
  mind: 'none',
  voice: 'none',
  heart: 'none',
  body: 'none',
  wings: 'none',
  roots: 'none',
};

const DIMENSIONS: Record<DimKey, DimensionData> = {
  mind: {
    name: 'Mind',
    descriptor: 'Understanding & Insight',
    color: 'var(--dim-mind, #7ea3cc)',
    bg: 'rgba(126,163,204,0.12)',
    meaning:
      'How you learn, analyze, and apply knowledge. Mind grows through research, critical thinking, mentoring, and helping others make better decisions.',
  },
  body: {
    name: 'Body',
    descriptor: 'Action & Execution',
    color: 'var(--dim-body, #6aab7a)',
    bg: 'rgba(106,171,122,0.12)',
    meaning:
      'What you do. Hands-on work, task completion, and showing up when it matters. Body reflects your ability to turn ideas into real outcomes.',
  },
  heart: {
    name: 'Heart',
    descriptor: 'Connection & Compassion',
    color: 'var(--dim-heart, #c77a6f)',
    bg: 'rgba(199,122,111,0.12)',
    meaning:
      'How far your compassion reaches. Heart grows when you care beyond your own circle — supporting communities you don\u2019t belong to, building bridges across distance, and strengthening global solidarity.',
  },
  wings: {
    name: 'Wings',
    descriptor: 'Support & Enablement',
    color: 'var(--dim-wings, #c9a24e)',
    bg: 'rgba(201,162,78,0.12)',
    meaning:
      'What you provide to lift others. Financial support, resources, and opportunities that make action possible. Wings amplify impact beyond your own effort.',
  },
  roots: {
    name: 'Roots',
    descriptor: 'Commitment & Steadfastness',
    color: 'var(--dim-roots, #8B7355)',
    bg: 'rgba(139,115,85,0.12)',
    meaning:
      'How deeply you\u2019re grounded. Integrity, follow-through, and showing up when it matters. Roots reflect trustworthiness — doing what you say you\u2019ll do and standing firm for the communities you serve.',
  },
  voice: {
    name: 'Voice',
    descriptor: 'Awareness & Influence',
    color: 'var(--dim-voice, #5aabb5)',
    bg: 'rgba(90,171,181,0.12)',
    meaning:
      'How you inspire others. Sharing ideas, advocating for causes, and bringing new people into the mission. Voice turns individual effort into collective momentum.',
  },
};

/** Label positions (SVG coords). Ordered: mind, voice, heart, body, wings, roots */
const LABEL_POSITIONS: Record<DimKey, { x: number; y: number; anchor: string }> = {
  mind:  { x: 210,  y: 36,  anchor: 'middle' },
  voice: { x: 346,  y: 116, anchor: 'start' },
  heart: { x: 346,  y: 268, anchor: 'start' },
  body:  { x: 210,  y: 350, anchor: 'middle' },
  wings: { x: 74,   y: 268, anchor: 'end' },
  roots: { x: 74,   y: 116, anchor: 'end' },
};

/* ── Medal display helpers ── */

const TIER_ORDER: TierLevel[] = ['none', 'bronze', 'silver', 'gold', 'platinum'];

const TIER_LABELS: Record<TierLevel, string> = {
  none: '—',
  bronze: 'Brz',
  silver: 'Ag',
  gold: 'Au',
  platinum: 'Pt',
};

/** Given a tier, return the next tier up (or null if at platinum). */
function nextTier(tier: TierLevel): TierLevel | null {
  const idx = TIER_ORDER.indexOf(tier);
  return idx < TIER_ORDER.length - 1 ? TIER_ORDER[idx + 1] : null;
}

/**
 * Find the highest tier that ALL 6 dimensions have reached or surpassed.
 * This is the "floor" — the metallic fill persists even as individual
 * dimensions advance, until all six have reached the next level.
 *
 * Examples:
 *   all bronze          → bronze
 *   5 silver + 1 bronze → bronze (not all at silver yet)
 *   all silver          → silver
 *   3 gold + 3 silver   → silver
 *   all platinum        → platinum
 *   all none            → null (no fill)
 */
function getUniformTier(t: Record<string, TierLevel>): TierLevel | null {
  const indices = DIM_KEYS.map((k) => TIER_ORDER.indexOf(t[k] || 'none'));
  const minIdx = Math.min(...indices);
  // 'none' (index 0) means no tier earned yet — no fill
  if (minIdx <= 0) return null;
  return TIER_ORDER[minIdx];
}

/* ── Component ── */

const DimensionRadar: React.FC<DimensionRadarProps> = ({
  tiers = EMPTY_TIERS,
  mode = 'dashboard',
  communityGuids = [],
  onDimensionSelect,
  viewerRung,
  debugMode = false,
}) => {
  const { t } = useAppTranslation('common');

  /** DEV-1489 — localized "Unlocks at <rung>" for a next-rung tease. */
  const lockLabelFor = useCallback(
    (rung: Rung) =>
      t('impact.unlocksAtRung', {
        defaultValue: 'Unlocks at {{rung}}',
        rung: t(`impact.rungName.${rung}`, { defaultValue: rung }),
      }),
    [t],
  );

  /**
   * The Impact Activity Catalog (DEV-1480) — the single source of truth for what
   * actually earns credit. The "how to grow it" list is built from these ENABLED
   * rows, never from hardcoded copy, so the radar and the emission engine cannot
   * disagree. Platform-wide and cached, so this is cheap on every mount.
   */
  const catalogActivities = useApiImpactActivities();

  // Reviewer round 2026-07-09: the dashboard overview carries the sanctioned
  // OVERALL status heading above the medal grid — same shared helper + same
  // 'pages' keys as the rail card, so gateway and destination never disagree.
  const { t: tPages } = useAppTranslation('pages');
  const [activeDim, setActiveDim] = useState<DimKey | null>(null);
  /* DEV-1211 — Modal-on-medal-click. Tracks which dimension's modal is
     currently open (null = closed). Independent from activeDim, which
     drives the radar's own highlight + the right-side slide-in detail
     panel for radar-label clicks. */
  const [medalModalDim, setMedalModalDim] = useState<DimKey | null>(null);
  const INTRO_SEEN_KEY = 'l4e_impact_sig_intro_done';
  const hasSeenIntro = useMemo(() => {
    try { return window?.localStorage?.getItem(INTRO_SEEN_KEY) === '1'; } catch { return false; }
  }, []);

  // SVG build animation (loading → animate-in → labels-in → alive) ALWAYS plays —
  // it's the "reveal your signature" moment that builds suspense.
  // Layout intro (slide-left + delayed text reveal) is one-time only.
  // On return visits: layout is final position, text is visible, but the radar
  // still draws itself.
  const isProfile = mode === 'profile';
  // Profile mode: skip build animation entirely — show final state immediately
  const [animPhase, setAnimPhase] = useState<'loading' | 'animate-in' | 'labels-in' | 'alive'>(isProfile ? 'alive' : 'loading');
  const [settled, setSettled] = useState(isProfile || hasSeenIntro);
  const [rightVisible, setRightVisible] = useState(isProfile || hasSeenIntro);
  const svgRef = useRef<SVGSVGElement>(null);

  /* ── Debug mode: local tier overrides ── */
  const [debugTiers, setDebugTiers] = useState<Record<string, TierLevel> | null>(null);
  const effectiveTiers = debugMode && debugTiers ? debugTiers : tiers;

  const handleDebugCycle = useCallback((dim: DimKey) => {
    if (!debugMode) return;
    setDebugTiers((prev) => {
      const base = prev ?? { ...tiers };
      const currentIdx = TIER_ORDER.indexOf(base[dim] || 'none');
      const nextIdx = (currentIdx + 1) % TIER_ORDER.length;
      return { ...base, [dim]: TIER_ORDER[nextIdx] };
    });
  }, [debugMode, tiers]);

  const resetDebugTiers = useCallback(() => setDebugTiers(null), []);

  /* ── Uniform tier detection for cascading celebration ── */
  const uniformTier = useMemo(() => getUniformTier(effectiveTiers), [effectiveTiers]);

  /** Map uniform tier to the CSS data-attribute value for cascade targeting */
  const uniformTierIdx = uniformTier ? TIER_ORDER.indexOf(uniformTier) : -1;

  /* ── Tier-up crystallization animation ── */
  const prevUniformRef = useRef<TierLevel | null>(null);
  const [tierUpPhase, setTierUpPhase] = useState<
    'idle' | 'dissolve' | 'crystallize' | 'shockwave' | 'settle'
  >('idle');

  useEffect(() => {
    const prev = prevUniformRef.current;
    prevUniformRef.current = uniformTier;

    // Only animate when the floor tier advances (not on initial mount or downgrade)
    if (
      prev !== null &&
      uniformTier !== null &&
      prev !== uniformTier &&
      TIER_ORDER.indexOf(uniformTier) > TIER_ORDER.indexOf(prev) &&
      animPhase === 'alive'
    ) {
      const timers: ReturnType<typeof setTimeout>[] = [];
      const t = (fn: () => void, ms: number) => {
        timers.push(setTimeout(fn, ms));
      };

      setTierUpPhase('dissolve');
      t(() => setTierUpPhase('crystallize'), 600);
      t(() => setTierUpPhase('shockwave'), 1400);
      t(() => setTierUpPhase('settle'), 2200);
      t(() => setTierUpPhase('idle'), 2800);

      return () => timers.forEach(clearTimeout);
    }
  }, [uniformTier, animPhase]);

  // Build user shape polygon points from tier data (wire follows actual tiers)
  const userShapePoints = DIM_KEYS.map((key, idx) => {
    const tier = effectiveTiers[key] || 'none';
    const r = TIER_RADII[tier];
    return hexVertex(r, ANGLES_DEG[idx]).join(',');
  }).join(' ');

  // Floor-tier hexagon — regular hex at the uniform tier radius (metallic fill stays here)
  const floorShapePoints = uniformTier ? hexPolygonPoints(TIER_RADII[uniformTier]) : '';

  // Dot positions (on the tier ring for that dimension)
  const dotPositions = DIM_KEYS.map((key, idx) => {
    const tier = effectiveTiers[key] || 'none';
    const r = TIER_RADII[tier];
    const [cx, cy] = hexVertex(r, ANGLES_DEG[idx]);
    return { key, cx, cy, tier };
  });

  // ── Animation orchestration ──
  // The SVG build animation (layers, hexes, dots, labels) always plays — it's
  // the "reveal your signature" moment. The LAYOUT animation (slide-left,
  // delayed text panel) only plays on the first visit.
  useEffect(() => {
    // Profile mode: no animation — already at 'alive' from initial state
    if (isProfile) return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => {
      timers.push(setTimeout(fn, ms));
    };

    // SVG build always plays
    t(() => setAnimPhase('animate-in'), 100);
    t(() => setAnimPhase('labels-in'), 4900);
    t(() => setAnimPhase('alive'), 6700);

    if (!hasSeenIntro) {
      // First visit: layout slide + delayed text reveal
      t(() => setSettled(true), 4100);
      t(() => setRightVisible(true), 5500);
      t(() => {
        try { window?.localStorage?.setItem(INTRO_SEEN_KEY, '1'); } catch { /* ignore */ }
      }, 6700);
    }
    // Return visits: settled=true and rightVisible=true from initial state,
    // so layout is already final — radar draws itself, text is visible.

    return () => timers.forEach(clearTimeout);
  }, [hasSeenIntro, isProfile]);

  // ── Interaction handlers ──
  const showOverview = useCallback(() => {
    setActiveDim(null);
    onDimensionSelect?.(null);
  }, [onDimensionSelect]);

  const toggleDimension = useCallback((key: DimKey) => {
    setActiveDim((prev) => {
      const next = prev === key ? null : key;
      onDimensionSelect?.(next);
      return next;
    });
  }, [onDimensionSelect]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') showOverview();
    },
    [showOverview],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const activeData = activeDim ? DIMENSIONS[activeDim] : null;

  /**
   * The ENABLED activities for the selected dimension (DEV-1480).
   *
   * `byDimension` comes from the impact_activities catalog — the same rows the
   * emission engine honors. An empty array is a legitimate, expected state (no
   * act in this dimension is wired yet), and the panel renders no growth list at
   * all in that case rather than falling back to hardcoded copy.
   *
   * catalogActivities.byDimension is async-hook data: it starts EMPTY and
   * populates when the request resolves, so it MUST be in this memo's deps
   * (CLAUDE.md — omitting an async-hook value is the DEV-932 latent-bug class).
   */
  const activeActivities = useMemo(
    () =>
      gateActivitiesByRung(
        activeDim ? catalogActivities.byDimension[activeDim] ?? [] : [],
        viewerRung,
      ),
    [activeDim, catalogActivities.byDimension, viewerRung],
  );

  // SVG class list
  const svgClasses = [
    styles.radarSvg,
    animPhase === 'loading' ? styles.loading : '',
    animPhase === 'animate-in' ? styles.animateIn : '',
    animPhase === 'labels-in' ? styles.labelsIn : '',
    animPhase === 'alive' ? styles.alive : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`${styles.radarWrapper} ${isProfile ? styles.profileMode : styles.dashboardMode}`}>
      <div className={styles.radarRow}>
        {/* ═══ LEFT: Radar SVG ═══ */}
        <div
          className={`${styles.radarLeft} ${settled ? styles.settled : ''}`}
        >
          <svg
            ref={svgRef}
            className={svgClasses}
            viewBox="0 0 420 400"
            data-uniform-tier={uniformTier || ''}
            data-uniform-idx={uniformTierIdx > 0 ? uniformTierIdx : ''}
          >
            <defs>
              <filter id="wire-glow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
                <feColorMatrix
                  in="blur"
                  type="matrix"
                  values="1 0 0 0 0.1  0 1 0 0 0.1  0 0 1 0 0.2  0 0 0 1.5 0"
                  result="c"
                />
                <feMerge>
                  <feMergeNode in="c" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="dot-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="frost-plat" x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="frost-gold" x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="frost-silv" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="frost-brnz" x="-25%" y="-25%" width="150%" height="150%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="spoke-glow" filterUnits="userSpaceOnUse" x="0" y="0" width="420" height="400">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              {/* ── Metallic gradient fills for uniform-tier shape ── */}
              <linearGradient id="grad-bronze" x1="0%" y1="0%" x2="100%" y2="30%">
                <stop offset="0%" stopColor="#8B5E34" />
                <stop offset="18%" stopColor="#CD7F32" />
                <stop offset="35%" stopColor="#E8A84C" />
                <stop offset="48%" stopColor="#FFC87C" />
                <stop offset="55%" stopColor="#E8A84C" />
                <stop offset="70%" stopColor="#CD7F32" />
                <stop offset="88%" stopColor="#A0642A" />
                <stop offset="100%" stopColor="#8B5E34" />
              </linearGradient>
              <linearGradient id="grad-silver" x1="0%" y1="0%" x2="100%" y2="30%">
                <stop offset="0%" stopColor="#7A8490" />
                <stop offset="18%" stopColor="#A8B4C0" />
                <stop offset="35%" stopColor="#C8D0D8" />
                <stop offset="48%" stopColor="#F0F4F8" />
                <stop offset="55%" stopColor="#C8D0D8" />
                <stop offset="70%" stopColor="#A8B4C0" />
                <stop offset="88%" stopColor="#8A96A4" />
                <stop offset="100%" stopColor="#7A8490" />
              </linearGradient>
              <linearGradient id="grad-gold" x1="0%" y1="0%" x2="100%" y2="30%">
                <stop offset="0%" stopColor="#B8962E" />
                <stop offset="18%" stopColor="#D4AF37" />
                <stop offset="35%" stopColor="#E8C84A" />
                <stop offset="48%" stopColor="#FFF4B0" />
                <stop offset="55%" stopColor="#FFD700" />
                <stop offset="70%" stopColor="#D4AF37" />
                <stop offset="88%" stopColor="#B8962E" />
                <stop offset="100%" stopColor="#9A7C22" />
              </linearGradient>
              <linearGradient id="grad-platinum" x1="0%" y1="0%" x2="100%" y2="30%">
                <stop offset="0%" stopColor="#2A2D32" />
                <stop offset="15%" stopColor="#3E4248" />
                <stop offset="30%" stopColor="#555B63" />
                <stop offset="44%" stopColor="#7A8290" />
                <stop offset="56%" stopColor="#555B63" />
                <stop offset="72%" stopColor="#3E4248" />
                <stop offset="88%" stopColor="#2A2D32" />
                <stop offset="100%" stopColor="#222528" />
              </linearGradient>

              {/* Perimeter glow filter for the filled shape — tight, subtle */}
              <filter id="shape-glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Frosted tier zones (outer to inner in DOM, animated inner to outer) */}
            <polygon
              className={styles.tierZone}
              data-tier="platinum"
              points={hexPolygonPoints(140)}
              fill="var(--medal-platinum, #C8C6C2)"
              fillOpacity={0.06}
              stroke="var(--medal-platinum, #C8C6C2)"
              strokeWidth={1.2}
              strokeOpacity={0.4}
              filter="url(#frost-plat)"
            />
            <polygon
              className={styles.tierZone}
              data-tier="gold"
              points={hexPolygonPoints(105)}
              fill="var(--medal-gold, #D4A843)"
              fillOpacity={0.08}
              stroke="var(--medal-gold, #D4A843)"
              strokeWidth={0.8}
              strokeOpacity={0.35}
              filter="url(#frost-gold)"
            />
            <polygon
              className={styles.tierZone}
              data-tier="silver"
              points={hexPolygonPoints(70)}
              fill="var(--medal-silver, #B0B8C4)"
              fillOpacity={0.1}
              stroke="var(--medal-silver, #B0B8C4)"
              strokeWidth={0.7}
              strokeOpacity={0.3}
              filter="url(#frost-silv)"
            />
            <polygon
              className={styles.tierZone}
              data-tier="bronze"
              points={hexPolygonPoints(35)}
              fill="var(--medal-bronze, #CD7F32)"
              fillOpacity={0.15}
              stroke="var(--medal-bronze, #CD7F32)"
              strokeWidth={0.6}
              strokeOpacity={0.4}
              filter="url(#frost-brnz)"
            />

            {/* Spokes */}
            {ANGLES_DEG.map((angle, i) => {
              const [x2, y2] = hexVertex(140, angle);
              return (
                <line
                  key={`spoke-${i}`}
                  className={styles.spoke}
                  x1={CX}
                  y1={CY}
                  x2={x2}
                  y2={y2}
                  stroke="var(--text-secondary)"
                  strokeWidth={0.4}
                  opacity={0.2}
                />
              );
            })}

            {/* Spoke highlights */}
            {DIM_KEYS.map((key, i) => {
              const [x2, y2] = hexVertex(140, ANGLES_DEG[i]);
              return (
                <line
                  key={`spoke-hl-${key}`}
                  x1={CX}
                  y1={CY}
                  x2={x2}
                  y2={y2}
                  stroke={DIMENSIONS[key].color}
                  strokeWidth={2}
                  opacity={activeDim === key ? 0.6 : 0}
                  filter="url(#spoke-glow)"
                  style={{ transition: 'opacity 0.2s ease' }}
                />
              );
            })}

            {/* Metallic fill — locked at floor tier hexagon (stays as cube) */}
            {uniformTier && (
              <>
                {/* Perimeter glow — soft halo around the floor hex */}
                <polygon
                  className={styles.shapeGlow}
                  points={floorShapePoints}
                  fill="none"
                  stroke={`url(#grad-${uniformTier})`}
                  strokeWidth={4}
                  strokeLinejoin="round"
                  strokeOpacity={0.3}
                  filter="url(#shape-glow)"
                />
                {/* Metallic fill — regular hexagon at floor tier radius */}
                <polygon
                  className={[
                    styles.userShape,
                    styles.userShapeFilled,
                    tierUpPhase === 'dissolve' ? styles.tierUpDissolve : '',
                    tierUpPhase === 'crystallize' ? styles.tierUpCrystallize : '',
                    tierUpPhase === 'settle' ? styles.tierUpSettle : '',
                  ].filter(Boolean).join(' ')}
                  points={floorShapePoints}
                  fill={`url(#grad-${uniformTier})`}
                  fillOpacity={0.55}
                  stroke={`url(#grad-${uniformTier})`}
                  strokeWidth={3}
                  strokeLinejoin="round"
                  filter="url(#wire-glow)"
                />
                {/* Isometric cube shading — three face overlays on the floor hex.
                    Uses fixed hex vertices at the floor tier radius.
                    Top face (lightest): Roots → Mind → Voice → Center
                    Right face (mid):    Voice → Heart → Body → Center
                    Left face (darkest): Body → Wings → Roots → Center */}
                {(() => {
                  const r = TIER_RADII[uniformTier];
                  const verts = ANGLES_DEG.map((a) => hexVertex(r, a));
                  const topFace = `${verts[5].join(',')} ${verts[0].join(',')} ${verts[1].join(',')} ${CX},${CY}`;
                  const rightFace = `${verts[1].join(',')} ${verts[2].join(',')} ${verts[3].join(',')} ${CX},${CY}`;
                  const leftFace = `${verts[3].join(',')} ${verts[4].join(',')} ${verts[5].join(',')} ${CX},${CY}`;
                  const isCrystallizing = tierUpPhase === 'crystallize';
                  return (
                    <>
                      <polygon
                        className={`${styles.cubeFace} ${isCrystallizing ? styles.cubeFaceTopIn : ''}`}
                        points={topFace}
                        fill="white"
                        fillOpacity={0.08}
                      />
                      <polygon
                        className={`${styles.cubeFace} ${isCrystallizing ? styles.cubeFaceRightIn : ''}`}
                        points={rightFace}
                        fill="black"
                        fillOpacity={0.04}
                      />
                      <polygon
                        className={`${styles.cubeFace} ${isCrystallizing ? styles.cubeFaceLeftIn : ''}`}
                        points={leftFace}
                        fill="black"
                        fillOpacity={0.10}
                      />
                    </>
                  );
                })()}

                {/* Shockwave — single radial pulse from center */}
                {tierUpPhase === 'shockwave' && (
                  <circle
                    className={styles.shockwave}
                    cx={CX}
                    cy={CY}
                    r={4}
                    fill="none"
                    stroke={`url(#grad-${uniformTier})`}
                    strokeWidth={3}
                  />
                )}
              </>
            )}

            {/* User shape — luminous wire (always follows actual tier positions) */}
            <polygon
              className={styles.userShape}
              points={userShapePoints}
              fill="none"
              stroke="var(--text-primary)"
              strokeWidth={2.5}
              strokeLinejoin="round"
              filter="url(#wire-glow)"
            />

            {/* Hit zones */}
            {DIM_KEYS.map((key) => (
              <polygon
                key={`zone-${key}`}
                className={styles.dimZone}
                points={getHitZonePoints(key)}
                fill="transparent"
                onClick={() => toggleDimension(key)}
                onMouseEnter={() => {
                  if (!activeDim && svgRef.current) {
                    // Hover highlight
                  }
                }}
              />
            ))}

            {/* Ripple rings for center dots (tier='none') — earthquake-style rings */}
            {animPhase === 'alive' && dotPositions
              .filter(({ tier }) => tier === 'none')
              .map(({ key, cx, cy }, dimIdx) =>
                [0, 1, 2].map((ring) => (
                  <circle
                    key={`ripple-${key}-${ring}`}
                    className={styles.rippleRing}
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill="none"
                    stroke="var(--text-primary, #ffffff)"
                    strokeWidth={4}
                    strokeOpacity={0.22}
                    style={{ animationDelay: `${dimIdx * 0.8 + ring * 2}s` }}
                  />
                ))
              )}

            {/* Dimension dots — white when at center (none tier), colored when earned */}
            {dotPositions.map(({ key, cx, cy, tier }) => (
              <circle
                key={`dot-${key}`}
                className={`${styles.dimDot}${tier === 'none' ? ` ${styles.centerDot}` : ''}`}
                data-dim={key}
                cx={cx}
                cy={cy}
                r={tier === 'none' ? 4 : 5.5}
                fill={tier === 'none' ? 'var(--text-primary, #ffffff)' : DIMENSIONS[key].color}
                filter={tier === 'none' ? undefined : 'url(#dot-glow)'}
                opacity={tier === 'none' ? 0.5 : 0.95}
                onClick={() => toggleDimension(key)}
              />
            ))}

            {/* Labels */}
            {DIM_KEYS.map((key) => {
              const pos = LABEL_POSITIONS[key];
              return (
                <text
                  key={`label-${key}`}
                  className={`${styles.dimLabel} ${activeDim === key ? styles.activeLabel : ''}`}
                  data-dim={key}
                  x={pos.x}
                  y={pos.y}
                  textAnchor={pos.anchor}
                  fill={DIMENSIONS[key].color}
                  fontSize={13}
                  fontWeight={700}
                  /* Skip 2026-07-10: on the dashboard the dimension WORD
                     opens the explainer modal — the same one the medal
                     cards open — instead of the right-pane detail swap.
                     Profile mode keeps the compact toggle behavior. */
                  onClick={() => (isProfile ? toggleDimension(key) : setMedalModalDim(key))}
                >
                  {DIMENSIONS[key].name}
                </text>
              );
            })}
          </svg>
        </div>

        {/* ═══ RIGHT: Content area ═══ */}
        <div className={`${styles.radarRight} ${rightVisible ? styles.visible : ''}`}>
          {/* Overview (default state) */}
          <div
            className={`${styles.rightContent} ${!activeDim ? styles.visible : ''}`}
          >
            <div className={styles.overviewPanel}>
              {/* Explanatory text removed — moved to ExplainRadarModal (DEV-678) */}

              {/* Overall-signature heading (dashboard only; sanctioned label —
                  per-dimension WORDS stay rejected, this is the one overall
                  status line, mirroring the rail card via overallSignature). */}
              {!isProfile && (() => {
                const { lit, overall } = overallSignature(effectiveTiers);
                return (
                  <h3 className={styles.overviewHeading}>
                    {overall
                      ? tPages('impact.card.signatureTier', { tier: tPages(`impact.tier.${overall}`) })
                      : tPages('impact.card.litCount', { count: lit })}
                  </h3>
                );
              })()}

              {/* ── Dimension medal grid (2 cols × 3 rows) ──
                  DEV-1211 — Replaced the horizontal-pill dimCard list with
                  a 2×3 grid of hexagonal embossed medals. The medal carries
                  the dimension icon stamped into its field; the dimension
                  name + descriptor sit above; the medal name (BRONZE /
                  SILVER / GOLD / PLATINUM) sits below. Click behavior is
                  unchanged — toggleDimension(key) still swaps the right
                  panel to the dimension's detail view. No counts /
                  progress hints until the tier-promotion reward layer is
                  built (see docs/platform/Impact_Signature_Instruction_Surface_Notes.md). */}
              <div className={styles.dimCardSection}>
                <div className={styles.dimCardGrid}>
                  {DIM_KEYS.map((key) => {
                    const tier = effectiveTiers[key] || 'none';
                    const tierLabel = tier === 'none'
                      ? '—'
                      : (tier as string).toUpperCase();
                    return (
                      <div
                        key={key}
                        className={`${styles.dimCard} ${debugMode ? styles.medalClickable : ''}`}
                        style={{
                          '--dim-tint': DIMENSIONS[key].bg,
                          '--dim-color': DIMENSIONS[key].color,
                        } as React.CSSProperties}
                        onClick={() => debugMode ? handleDebugCycle(key) : setMedalModalDim(key)}
                      >
                        <div className={styles.medalCardHeader}>
                          <span className={styles.medalCardName}>{DIMENSIONS[key].name}</span>
                          <span className={styles.medalCardDesc}>{DIMENSIONS[key].descriptor}</span>
                        </div>
                        <div className={styles.medalCardMedal}>
                          <DimensionMedal
                            tier={tier}
                            dim={key}
                            ariaLabel={`${DIMENSIONS[key].name} — ${tier === 'none' ? 'not yet earned' : tier}`}
                          />
                        </div>
                        <div className={`${styles.medalCardLabel} ${styles[`medalLabel_${tier}`]}`}>
                          {tierLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {debugMode && debugTiers && (
                <button
                  className={styles.debugReset}
                  onClick={resetDebugTiers}
                >
                  {t('impact.resetTiers', { defaultValue: 'Reset tiers' })}
                </button>
              )}
            </div>
          </div>

          {/* Dimension detail (shown on click) */}
          <div
            className={`${styles.rightContent} ${activeDim ? styles.visible : ''}`}
          >
            {activeData && activeDim && (() => {
              const dimTier = effectiveTiers[activeDim] || 'none';
              const next = nextTier(dimTier);
              const IconComp = DIM_ICONS[activeDim];
              return (
                <div className={styles.detailPanel} style={{ borderInlineStartColor: activeData.color }}>
                  {/* ── Header: icon + name + back button ── */}
                  <div className={styles.panelHeader}>
                    <div className={styles.panelIdentity}>
                      <span className={styles.panelIcon} style={{ color: activeData.color }}>
                        {IconComp && <IconComp color={activeData.color} size={28} />}
                      </span>
                      <div>
                        <div className={styles.panelDimName}>{activeData.name}</div>
                        <div className={styles.panelDescriptor}>{activeData.descriptor}</div>
                      </div>
                    </div>
                    <button className={styles.panelBack} onClick={showOverview}>
                      {'\u2190'} {t('dimensionRadar.allDimensions')}
                    </button>
                  </div>

                  {/* ── Compact tier pill — matches the overview tinted pills ── */}
                  <div
                    className={styles.detailTierRow}
                    style={{
                      '--dim-tint': activeData.bg,
                      '--dim-color': activeData.color,
                    } as React.CSSProperties}
                  >
                    <div className={`${styles.detailTierBadgeWrap} ${styles[`badge_${dimTier}`]}`}>
                      <div className={styles.dimCardBadgeBorder} />
                      <div className={styles.dimCardBadge}>
                        {dimTier === 'none' ? '—' : TIER_LABELS[dimTier]}
                      </div>
                    </div>
                    <div className={styles.detailTierInfo}>
                      <span className={styles.detailTierName}>
                        {dimTier === 'none'
                          ? t('impact.noTierYet', { defaultValue: 'No tier yet' })
                          : dimTier.charAt(0).toUpperCase() + dimTier.slice(1)}
                      </span>
                      {next ? (
                        <button className={styles.nextTierLink}>
                          {t('impact.reachNext', { defaultValue: 'Reach {{tier}} →', tier: next.charAt(0).toUpperCase() + next.slice(1) })}
                        </button>
                      ) : (
                        <span className={styles.nextTierLink}>
                          {t('impact.keepGrowing', { defaultValue: 'Keep inspiring others →' })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Content sections ── */}
                  <div className={styles.detailSections}>
                    {/* What it means */}
                    <div className={styles.detailSection}>
                      <h4>{t('impact.whatItMeans', { defaultValue: 'What it means' })}</h4>
                      <p>{activeData.meaning}</p>
                    </div>

                    {/* ── How to grow it — FROM THE CATALOG (DEV-1480) ──
                        Every item here is an ENABLED impact_activities row, which
                        means the emission engine will actually credit it. This list
                        and ImpactEmission read the same table, so the radar can no
                        longer promise credit that never arrives.

                        When a dimension has NO enabled activities, we render
                        NOTHING — not a fallback list, not a greyed placeholder.
                        Today that is the case for Mind, Body, Heart and Wings: no
                        act in those dimensions is wired yet. Showing suggestions
                        there would be a lie, and hiding the section is the honest
                        alternative. The silence is the feature. */}
                    {!isProfile && activeActivities.length > 0 && (
                      <div className={styles.detailSection}>
                        <h4>{t('impact.howToGrowIt', { defaultValue: 'How to grow it' })}</h4>
                        <ul>
                          {activeActivities.map(({ activity: a, locked, unlocksAt }) => (
                            <li
                              key={a.activityKey}
                              className={locked ? styles.growthLocked : undefined}
                              title={
                                locked && unlocksAt
                                  ? lockLabelFor(unlocksAt)
                                  : localizedActivity(a, 'displayHint')
                              }
                            >
                              {locked && (
                                <Lock size={12} className={styles.growthLockIcon} aria-hidden="true" />
                              )}
                              <span>{localizedActivity(a, 'displayTitle')}</span>
                              {locked && unlocksAt && (
                                <span className={styles.growthLockLabel}>{lockLabelFor(unlocksAt)}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {debugMode && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
          Mode: {mode} | Phase: {animPhase} | Active: {activeDim || 'none'}
          {uniformTier && ` | Uniform: ${uniformTier}`}
          {debugTiers && ' | (debug override)'}
          <br />
          Tiers: {DIM_KEYS.map((k) => `${k}=${effectiveTiers[k] || 'none'}`).join(', ')}
        </div>
      )}

      {/* DEV-1211 — Modal-on-medal-click. Renders a centered overlay
          with the dimension's identity, current medal, meaning, and
          growth list. Closed via X button, backdrop click, or Escape. */}
      {medalModalDim && (
        <DimensionMedalModal
          dim={medalModalDim}
          tier={effectiveTiers[medalModalDim] || 'none'}
          name={DIMENSIONS[medalModalDim].name}
          descriptor={DIMENSIONS[medalModalDim].descriptor}
          color={DIMENSIONS[medalModalDim].color}
          meaning={DIMENSIONS[medalModalDim].meaning}
          /* Catalog-driven (DEV-1480) + rung-gated (DEV-1489): live acts, plus
             next-rung teases marked locked. May be empty, in which case the modal
             shows the meaning and no growth list. */
          growth={gateActivitiesByRung(
            catalogActivities.byDimension[medalModalDim] ?? [],
            viewerRung,
          ).map(({ activity: a, locked, unlocksAt }) => ({
            label: localizedActivity(a, 'displayTitle'),
            hint: localizedActivity(a, 'displayHint'),
            locked,
            lockLabel: locked && unlocksAt ? lockLabelFor(unlocksAt) : undefined,
          }))}
          onClose={() => setMedalModalDim(null)}
        />
      )}
    </div>
  );
};

/** Hit zone polygons — triangular slices from center */
function getHitZonePoints(dim: DimKey): string {
  const zones: Record<DimKey, string> = {
    mind:  '210,190 170,50 250,50',
    voice: '210,190 250,50 345,120 345,190',
    heart: '210,190 345,190 345,270 250,340',
    body:  '210,190 250,340 170,340',
    wings: '210,190 170,340 75,270 75,190',
    roots: '210,190 75,190 75,120 170,50',
  };
  return zones[dim];
}

export default DimensionRadar;
